// interlinked-tdd: exempt
import { Link, useNavigate } from "react-router";
import { PortalCards } from "@/components/PortalCards";
import { TOTAL_SERVER_COUNT } from "@/lib/themes";

interface DemoPrompt {
  category: string;
  title: string;
  prompt: string;
  servers: string[];
}

const DEMO_PROMPTS: DemoPrompt[] = [
  {
    category: "DRUG DISCOVERY",
    title: "Top targets for melanoma",
    prompt:
      "What are the top drug targets for melanoma? Cross-reference Open Targets, ChEMBL, and DGIdb.",
    servers: ["Open Targets", "ChEMBL", "DGIdb"],
  },
  {
    category: "VARIANT BIOLOGY",
    title: "BRAF V600E in context",
    prompt:
      "For BRAF V600E in melanoma, cross-reference CIViC for therapies, Open Targets for disease links, and Reactome for the MAPK pathway context.",
    servers: ["CIViC", "Open Targets", "Reactome"],
  },
  {
    category: "STRUCTURE",
    title: "KRAS protein structure",
    prompt:
      "Show me the protein structure of KRAS — pull the UniProt entry and any PDB structures.",
    servers: ["UniProt", "RCSB PDB"],
  },
  {
    category: "LITERATURE",
    title: "GLP-1 agonists in oncology",
    prompt:
      "Find recent PubMed papers on GLP-1 receptor agonists in oncology — surface the most cited.",
    servers: ["PubMed"],
  },
  {
    category: "RELATIONSHIP GRAPH",
    title: "Map EGFR's neighborhood",
    prompt:
      "Map the relationship neighborhood of EGFR across sources: target–disease links from Open Targets, variant therapies from CIViC, and pathway context from Reactome. Keep each relationship tied to its source.",
    servers: ["Open Targets", "CIViC", "Reactome"],
  },
  {
    category: "PIPELINE INTEL",
    title: "Osimertinib drug profile",
    prompt:
      "Build a profile for osimertinib: mechanism and targets from ChEMBL, plus clinical trials and phases from ClinicalTrials.gov.",
    servers: ["ChEMBL", "ClinicalTrials.gov"],
  },
  {
    category: "EVIDENCE SYNTHESIS",
    title: "Mini systematic review",
    prompt:
      "Run a mini evidence review on GLP-1 receptor agonists in Alzheimer's: search PubMed and ClinicalTrials.gov, screen for relevant studies, and summarize them in a table with citations.",
    servers: ["PubMed", "ClinicalTrials.gov"],
  },
];

interface Category {
  label: string;
  servers: string[];
}

const CATEGORIES: Category[] = [
  { label: "Genomics", servers: ["Ensembl", "UniProt", "CIViC", "Monarch"] },
  {
    label: "Drug Discovery",
    servers: ["ChEMBL", "DGIdb", "RxNorm", "FAERS", "OpenFDA"],
  },
  { label: "Cancer", servers: ["Open Targets", "NCI GDC", "Pharos"] },
  { label: "Structure", servers: ["RCSB PDB"] },
  {
    label: "Literature",
    servers: ["PubMed", "PubTator3", "Scholarly Graph", "DataCite"],
  },
  { label: "Clinical Trials", servers: ["ClinicalTrials.gov"] },
  { label: "Pathways", servers: ["Reactome", "STRING"] },
  { label: "Expression", servers: ["GTEx"] },
  { label: "Ontology", servers: ["Ontology Hub"] },
  {
    label: "Regulatory & Financial",
    servers: ["FDA Orange Book", "SEC EDGAR"],
  },
  { label: "Coding", servers: ["ICD-10"] },
];

export function Landing(): React.JSX.Element {
  const navigate = useNavigate();

  const tryPrompt = (prompt: string) => {
    navigate(`/chat?prompt=${encodeURIComponent(prompt)}`);
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Header />

      <main>
        <Hero onTryPrompt={() => tryPrompt(DEMO_PROMPTS[0].prompt)} />

        <section
          aria-label="Try a demo"
          className="border-t border-border"
          data-testid="landing-demos"
        >
          <div className="mx-auto max-w-5xl px-6 py-16">
            <div className="mb-8 max-w-2xl">
              <p className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
                Live demo · click to run
              </p>
              <h2 className="mt-2 text-2xl font-semibold tracking-tight md:text-3xl">
                Four questions you can ask right now.
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                Each prompt opens the chat with the question pre-filled. The
                model picks which databases to call and synthesizes the answer
                with citations. No signup.
              </p>
            </div>

            <ul className="grid grid-cols-1 gap-px overflow-hidden rounded-md border border-border bg-border md:grid-cols-2">
              {DEMO_PROMPTS.map((demo) => (
                <li key={demo.title} className="bg-background">
                  <button
                    type="button"
                    onClick={() => tryPrompt(demo.prompt)}
                    data-testid="landing-demo-card"
                    className="group flex h-full w-full flex-col gap-3 p-6 text-left transition-colors hover:bg-accent"
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-mono text-[10px] uppercase tracking-wider text-emerald-600 dark:text-emerald-400">
                        {demo.category}
                      </span>
                      <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground transition-colors group-hover:text-foreground">
                        Try →
                      </span>
                    </div>
                    <h3 className="text-lg font-semibold tracking-tight">
                      {demo.title}
                    </h3>
                    <p className="text-sm leading-relaxed text-muted-foreground">
                      {demo.prompt}
                    </p>
                    <div className="mt-auto flex flex-wrap gap-1.5 pt-3">
                      {demo.servers.map((s) => (
                        <span
                          key={s}
                          className="rounded border border-border px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground"
                        >
                          {s}
                        </span>
                      ))}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <PortalCards />

        <section
          aria-label="How it works"
          className="border-t border-border bg-muted/20"
        >
          <div className="mx-auto max-w-5xl px-6 py-16">
            <p className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
              How it works
            </p>
            <h2 className="mt-2 text-2xl font-semibold tracking-tight md:text-3xl">
              Ask a question. Get an answer with citations.
            </h2>
            <ol className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-3">
              <Step
                n="01"
                title="Pick a question"
                body="Click a demo above or type your own. Toggle which databases you want available in the chat header."
              />
              <Step
                n="02"
                title="The agent picks tools"
                body="The model reads tool schemas from the MCP servers and decides which ones to call — no orchestration code on your side."
              />
              <Step
                n="03"
                title="Synthesized answer"
                body="Tool outputs are summarized in plain language with the underlying records linked. Inspect any tool call to see the raw data."
              />
            </ol>
          </div>
        </section>

        <section
          aria-label="Database catalog"
          className="border-t border-border"
        >
          <div className="mx-auto max-w-5xl px-6 py-16">
            <div className="mb-8 max-w-2xl">
              <p className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
                What&apos;s wired in
              </p>
              <h2 className="mt-2 text-2xl font-semibold tracking-tight md:text-3xl">
                {TOTAL_SERVER_COUNT} scientific databases across three portals.
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                Each one is a Cloudflare Worker speaking the Model Context
                Protocol. The open-source monorepo has 100+ servers; the chat
                exposes {TOTAL_SERVER_COUNT}, themed as Genomics, Pharma, and Clinical — pick a
                domain above to enable a portal&apos;s full set.
              </p>
            </div>

            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
              {CATEGORIES.map((cat) => (
                <div
                  key={cat.label}
                  className="rounded-md border border-border bg-card p-4"
                >
                  <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                    {cat.label}
                  </p>
                  <ul className="mt-2 flex flex-wrap gap-1.5">
                    {cat.servers.map((s) => (
                      <li
                        key={s}
                        className="rounded border border-border px-1.5 py-0.5 font-mono text-[10px] text-foreground"
                      >
                        {s}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section
          aria-label="Use in your own client"
          className="border-t border-border"
        >
          <div className="mx-auto max-w-5xl px-6 py-16">
            <div className="grid grid-cols-1 items-center gap-8 md:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)]">
              <div>
                <p className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
                  Use in your own MCP client
                </p>
                <h2 className="mt-2 text-2xl font-semibold tracking-tight md:text-3xl">
                  Or skip the chat — wire these straight into Claude Desktop,
                  Claude Code, Cursor, or VS Code.
                </h2>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                  Pick the servers you want, copy the generated config, paste
                  it into your client. The servers are public HTTP endpoints —
                  no signup, no keys.
                </p>
                <div className="mt-6 flex flex-wrap gap-3">
                  <Link
                    to="/install"
                    data-testid="landing-cta-install"
                    className="inline-flex h-10 items-center rounded-md bg-foreground px-5 text-sm font-medium text-background transition-opacity hover:opacity-90"
                  >
                    Open the install page →
                  </Link>
                </div>
              </div>
              <div className="rounded-md border border-border bg-card p-4">
                <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                  .mcp.json (Claude Code)
                </p>
                <pre className="mt-3 overflow-x-auto font-mono text-[11px] leading-relaxed text-foreground/90">
                  <code>{`{
  "mcpServers": {
    "bio-entrez": {
      "type": "http",
      "url": "https://entrez-mcp-server.quentincody.workers.dev/mcp"
    },
    "bio-opentargets": {
      "type": "http",
      "url": "https://open-targets-mcp-server.quentincody.workers.dev/mcp"
    }
  }
}`}</code>
                </pre>
              </div>
            </div>
          </div>
        </section>

        <section
          aria-label="Get started"
          className="border-t border-border bg-muted/20"
        >
          <div className="mx-auto max-w-3xl px-6 py-16 text-center">
            <h2 className="text-2xl font-semibold tracking-tight md:text-3xl">
              Open source. No signup. No API keys to bring.
            </h2>
            <p className="mx-auto mt-3 max-w-xl text-sm leading-relaxed text-muted-foreground">
              Try the live demo, or self-host the whole thing on Cloudflare
              Workers. Everything below is Apache-2.0.
            </p>
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              <Link
                to="/chat"
                data-testid="landing-cta-chat"
                className="inline-flex h-10 items-center rounded-md bg-emerald-500 px-5 text-sm font-medium text-background transition-colors hover:bg-emerald-400"
              >
                Open the chat →
              </Link>
              <Link
                to="/install"
                data-testid="landing-cta-install-secondary"
                className="inline-flex h-10 items-center rounded-md border border-border bg-background px-5 text-sm font-medium text-foreground transition-colors hover:bg-accent"
              >
                Install in your client
              </Link>
              <a
                href="https://github.com/QuentinCody/bio-mcp-client"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex h-10 items-center rounded-md border border-border bg-background px-5 text-sm font-medium text-foreground transition-colors hover:bg-accent"
              >
                View on GitHub
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

function Header(): React.JSX.Element {
  return (
    <header className="sticky top-0 z-30 border-b border-border bg-background/80 backdrop-blur">
      <div className="mx-auto flex h-12 max-w-5xl items-center justify-between px-6">
        <Link to="/" className="flex items-center gap-2">
          <span className="grid size-6 place-items-center rounded-md bg-foreground font-mono text-[10px] font-bold text-background">
            B
          </span>
          <span className="text-sm font-semibold tracking-tight">Bio MCP Portal</span>
          <span className="hidden font-mono text-[10px] uppercase tracking-wider text-muted-foreground sm:inline">
            research console
          </span>
        </Link>
        <nav className="flex items-center gap-4 text-sm">
          <Link
            to="/install"
            data-testid="landing-nav-install"
            className="text-muted-foreground transition-colors hover:text-foreground"
          >
            Install
          </Link>
          <a
            href="https://github.com/QuentinCody/bio-mcp-client"
            target="_blank"
            rel="noopener noreferrer"
            className="text-muted-foreground transition-colors hover:text-foreground"
          >
            GitHub
          </a>
          <Link
            to="/chat"
            data-testid="landing-nav-chat"
            className="inline-flex h-7 items-center rounded-md bg-foreground px-3 text-xs font-medium text-background transition-opacity hover:opacity-90"
          >
            Open chat →
          </Link>
        </nav>
      </div>
    </header>
  );
}

function Hero({
  onTryPrompt,
}: {
  onTryPrompt: () => void;
}): React.JSX.Element {
  return (
    <section
      aria-label="Hero"
      className="mx-auto max-w-5xl px-6 pt-16 pb-20 md:pt-24 md:pb-28"
    >
      <p className="font-mono text-[11px] uppercase tracking-wider text-emerald-600 dark:text-emerald-400">
        Bio MCP Portal · open source · cloudflare-native
      </p>
      <h1 className="mt-3 max-w-3xl text-4xl font-semibold leading-[1.05] tracking-tight md:text-6xl">
        Query {TOTAL_SERVER_COUNT} scientific databases by chat.
      </h1>
      <p className="mt-5 max-w-2xl text-base leading-relaxed text-muted-foreground md:text-lg">
        PubMed, Open Targets, ChEMBL, ClinicalTrials.gov, UniProt, CIViC, RCSB
        PDB, Reactome, GTEx — all from one prompt. The model picks the tools,
        runs the queries, and synthesizes the answer. No signup, no API keys.
      </p>
      <div className="mt-8 flex flex-wrap gap-3">
        <button
          type="button"
          onClick={onTryPrompt}
          data-testid="landing-hero-cta"
          className="inline-flex h-10 items-center rounded-md bg-emerald-500 px-5 text-sm font-medium text-background transition-colors hover:bg-emerald-400"
        >
          Try a live demo →
        </button>
        <Link
          to="/chat"
          className="inline-flex h-10 items-center rounded-md border border-border bg-background px-5 text-sm font-medium text-foreground transition-colors hover:bg-accent"
        >
          Open the chat
        </Link>
      </div>
    </section>
  );
}

function Step({
  n,
  title,
  body,
}: {
  n: string;
  title: string;
  body: string;
}): React.JSX.Element {
  return (
    <li className="rounded-md border border-border bg-background p-5">
      <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
        {n}
      </p>
      <h3 className="mt-2 text-base font-semibold tracking-tight">{title}</h3>
      <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
        {body}
      </p>
    </li>
  );
}

function Footer(): React.JSX.Element {
  return (
    <footer className="border-t border-border">
      <div className="mx-auto flex max-w-5xl flex-col gap-4 px-6 py-8 text-xs text-muted-foreground sm:flex-row sm:items-center sm:justify-between">
        <div>
          Bio MCP Portal — research console · Apache-2.0 · built on Cloudflare Workers
        </div>
        <div className="flex items-center gap-4">
          <a
            href="https://github.com/QuentinCody/bio-mcp-client"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-foreground"
          >
            GitHub
          </a>
          <a
            href="https://modelcontextprotocol.io"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-foreground"
          >
            About MCP
          </a>
        </div>
      </div>
    </footer>
  );
}
